import { FormikHelpers, FormikProps, useFormik } from "formik";
import { useCallback } from "react";
import useDisclosure from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import {
  PAYER_INITIAL_VALUES,
  PAYER_MESSAGES,
  PayerFormValues,
} from "../payers-constant";
import { toPayerPayload } from "../payers-utils";
import { payerValidationSchema } from "../payers-validation";
import {
  useCreatePayer,
  usePayerIdExists,
  usePayerNameExists,
  usePayerUsageLookup,
  useUpdatePayer,
} from "./use-payers-api";

interface UsePayerFormParams {
  preFillValues?: PayerFormValues;
  onSaved: () => void;
}

interface UsePayerFormResult {
  formik: FormikProps<PayerFormValues>;
  isEditMode: boolean;
  discard: ReturnType<typeof useDisclosure>;
  warning: ReturnType<typeof useDisclosure>;
  onConfirmWarning: () => Promise<void>;
}

export const usePayerForm = ({
  preFillValues,
  onSaved,
}: UsePayerFormParams): UsePayerFormResult => {
  const isEditMode = !!preFillValues;
  const showSnackbar = useSnackbar();
  const discard = useDisclosure(false);
  const warning = useDisclosure(false);
  const { mutateAsync: createPayer } = useCreatePayer();
  const { mutateAsync: updatePayer } = useUpdatePayer();
  const payerNameExists = usePayerNameExists();
  const payerIdExists = usePayerIdExists();
  const lookupPayerUsage = usePayerUsageLookup();

  const save = useCallback(
    async (values: PayerFormValues) => {
      const payer = toPayerPayload(values);
      try {
        const response = preFillValues
          ? await updatePayer({ currentPayerId: preFillValues.payerId, payer })
          : await createPayer(payer);
        showSnackbar(getSuccessSnackbar(response));
        onSaved();
      } catch (error) {
        showSnackbar(getErrorSnackbar(error));
      }
    },
    [preFillValues, createPayer, updatePayer, showSnackbar, onSaved]
  );

  const onSubmit = useCallback(
    async (
      values: PayerFormValues,
      { setFieldError }: FormikHelpers<PayerFormValues>
    ) => {
      const isIdChanged = values.payerId.trim() !== preFillValues?.payerId;
      const isNameChanged =
        values.payerName.trim() !== preFillValues?.payerName;
      const [isIdTaken, isNameTaken] = await Promise.all([
        isIdChanged && payerIdExists(values.payerId),
        isNameChanged &&
          payerNameExists(values.payerName, preFillValues?.payerId),
      ]);
      if (isIdTaken) {
        setFieldError("payerId", PAYER_MESSAGES.PAYER_ID_EXISTS);
      }
      if (isNameTaken) {
        setFieldError("payerName", PAYER_MESSAGES.NAME_EXISTS);
      }
      if (isIdTaken || isNameTaken) return;

      if (isIdChanged && preFillValues) {
        const usage = await lookupPayerUsage(preFillValues.payerId).catch(
          () => undefined
        );
        if (usage && usage.openClaimCount > 0) {
          warning.open({ values, message: PAYER_MESSAGES.PAYER_ID_CHANGE });
          return;
        }
      }
      await save(values);
    },
    [
      preFillValues,
      payerIdExists,
      payerNameExists,
      lookupPayerUsage,
      warning,
      save,
    ]
  );

  const formik = useFormik({
    initialValues: preFillValues ?? PAYER_INITIAL_VALUES,
    validationSchema: payerValidationSchema,
    validateOnMount: true,
    onSubmit,
  });

  const onConfirmWarning = useCallback(async () => {
    const values: PayerFormValues = warning.data.values;
    warning.close();
    await save(values);
  }, [warning, save]);

  return { formik, isEditMode, discard, warning, onConfirmWarning };
};
